import { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import type {
  TimelineEvent,
  TimelineEra,
  TimelinePerson,
  TimelineScene,
} from "@/hooks/useTimelineData";

export type TimelineCanvasHandle = {
  navigateTo: (targetStart: number, targetEnd: number, duration?: number) => void;
};

type HoveredItem =
  | { type: "event"; item: TimelineEvent; x: number; y: number }
  | { type: "era"; item: TimelineEra; x: number; y: number }
  | { type: "person"; item: TimelinePerson; x: number; y: number }
  | { type: "scene"; item: TimelineScene; x: number; y: number }
  | null;

type Props = {
  events: TimelineEvent[];
  eras: TimelineEra[];
  people: TimelinePerson[];
  scenes: TimelineScene[];
  visiblePeople: string[]; // Ordered array - most recently selected last (renders at bottom)
  selectedSceneId: string | null;
  onHover: (item: HoveredItem) => void;
  onClick: (item: HoveredItem) => void;
  onSceneClick: (sceneId: string, x: number, y: number) => void;
  onContextMenu?: (date: string, x: number, y: number) => void;
};

const MIN_YEAR = 1875;
const MAX_YEAR = 2026;
const TIMELINE_Y = 380;
const PERSON_ROW_HEIGHT = 32;
const PERSON_START_Y = 160; // After scene track and staging area
const PERSON_PHOTO_SIZE = 24;

// Staging area for undated scenes
const STAGING_AREA_Y = 10;
const STAGING_AREA_HEIGHT = 36;
const STAGING_ICON_SIZE = 28;
const STAGING_ICON_GAP = 8;

// Scene track constants
const SCENE_TRACK_Y = 90;
const SCENE_TRACK_HEIGHT = 60;
const SCENE_ICON_SIZE = 24;
const SCENE_THUMB_SIZE = 48;

// Label stem heights (staggered)
const STEM_HEIGHTS = [45, 75, 105];
const LABEL_FONT_SIZE = 11;
const MIN_LABEL_SPACING = 120; // Minimum px between labels to show both

// Spellbook colors
const COLORS = {
  background: '#0a0a0f',
  surface: '#141419',
  border: '#1e1e26',
  borderSubtle: '#2a2a34',
  stem: '#3a3a42',
  muted: '#6b7280',
  labelText: '#9ca3af',
  labelTextHover: '#14b8a6',
  primary: '#14b8a6',
  primaryMuted: '#0d9488',
};

// Scene status colors
const SCENE_STATUS_COLORS: Record<string, string> = {
  draft: '#6B7280',    // gray
  approved: '#14B8A6', // teal
  killed: '#EF4444',   // red
  shot: '#22C55E',     // green
};

function parseDate(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.getFullYear() + d.getMonth() / 12 + d.getDate() / 365;
}

function formatYear(year: number): string {
  return Math.floor(year).toString();
}

// Mute era colors by reducing saturation
function muteColor(hex: string, opacity: number): string {
  return hex + Math.round(opacity * 255).toString(16).padStart(2, '0');
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export const TimelineCanvas = forwardRef<TimelineCanvasHandle, Props>(function TimelineCanvas({
  events,
  eras,
  people,
  scenes,
  visiblePeople,
  selectedSceneId,
  onHover,
  onClick,
  onSceneClick,
  onContextMenu,
}, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map());
  const animationRef = useRef<number | null>(null);

  const [viewStart, setViewStart] = useState(MIN_YEAR);
  const [viewEnd, setViewEnd] = useState(MAX_YEAR);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, viewStart: MIN_YEAR });
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);

  // Expose navigation method via ref
  useImperativeHandle(ref, () => ({
    navigateTo: (targetStart: number, targetEnd: number, duration = 500) => {
      // Cancel any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      const startTime = performance.now();
      const initialStart = viewStart;
      const initialEnd = viewEnd;

      // Easing function (ease-out cubic)
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);

        const newStart = initialStart + (targetStart - initialStart) * easedProgress;
        const newEnd = initialEnd + (targetEnd - initialEnd) * easedProgress;

        setViewStart(newStart);
        setViewEnd(newEnd);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          animationRef.current = null;
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    },
  }), [viewStart, viewEnd]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Load images for people
  useEffect(() => {
    for (const person of people) {
      if (person.imageUrl && !imageCache.current.has(`person_${person.id}`)) {
        const img = new Image();
        img.onload = () => {
          imageCache.current.set(`person_${person.id}`, img);
          setImagesLoaded((prev) => prev + 1);
        };
        img.onerror = () => {
          setImagesLoaded((prev) => prev + 1);
        };
        img.src = person.imageUrl;
      }
    }
  }, [people]);

  // Load images for events
  useEffect(() => {
    for (const event of events) {
      if (event.imageUrl && !imageCache.current.has(`event_${event.id}`)) {
        const img = new Image();
        img.onload = () => {
          imageCache.current.set(`event_${event.id}`, img);
          setImagesLoaded((prev) => prev + 1);
        };
        img.onerror = () => {
          // Mark as failed so we fall back to dot
          setImagesLoaded((prev) => prev + 1);
        };
        img.src = event.imageUrl;
      }
    }
  }, [events]);

  // Load images for scenes
  useEffect(() => {
    for (const scene of scenes) {
      if (scene.firstImageUrl && !imageCache.current.has(`scene_${scene.id}`)) {
        const img = new Image();
        img.onload = () => {
          imageCache.current.set(`scene_${scene.id}`, img);
          setImagesLoaded((prev) => prev + 1);
        };
        img.onerror = () => {
          setImagesLoaded((prev) => prev + 1);
        };
        img.src = scene.firstImageUrl;
      }
    }
  }, [scenes]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const yearToX = useCallback(
    (year: number): number => {
      const range = viewEnd - viewStart;
      return ((year - viewStart) / range) * dimensions.width;
    },
    [viewStart, viewEnd, dimensions.width]
  );

  const xToYear = useCallback(
    (x: number): number => {
      const range = viewEnd - viewStart;
      return viewStart + (x / dimensions.width) * range;
    },
    [viewStart, viewEnd, dimensions.width]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = dimensions;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Clear with background color
    ctx.fillStyle = COLORS.background;
    ctx.fillRect(0, 0, width, height);

    const range = viewEnd - viewStart;

    // Draw eras with muted colors
    for (const era of eras) {
      const startYear = parseDate(era.startDate);
      const endYear = parseDate(era.endDate);
      if (startYear === null || endYear === null) continue;

      const x1 = yearToX(startYear);
      const x2 = yearToX(endYear);

      // Era background - very subtle (reduced height)
      ctx.fillStyle = muteColor(era.color, 0.08);
      ctx.fillRect(x1, TIMELINE_Y - 25, x2 - x1, 50);

      // Era top border line
      ctx.strokeStyle = muteColor(era.color, 0.2);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x1, TIMELINE_Y - 25);
      ctx.lineTo(x2, TIMELINE_Y - 25);
      ctx.stroke();

      // Era label (if wide enough)
      if (x2 - x1 > 60) {
        ctx.fillStyle = muteColor(era.color, 0.6);
        ctx.font = "11px 'GT America', system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(era.title, (x1 + x2) / 2, TIMELINE_Y + 70, x2 - x1 - 10);
      }
    }

    // Draw year markers
    let yearStep = 1;
    if (range > 100) yearStep = 25;
    else if (range > 50) yearStep = 10;
    else if (range > 20) yearStep = 5;
    else if (range > 10) yearStep = 2;

    const startYearMarker = Math.ceil(viewStart / yearStep) * yearStep;
    ctx.strokeStyle = COLORS.border;
    ctx.fillStyle = COLORS.muted;
    ctx.font = "10px 'GT America', system-ui, sans-serif";
    ctx.textAlign = "center";

    for (let year = startYearMarker; year <= viewEnd; year += yearStep) {
      const x = yearToX(year);
      ctx.beginPath();
      ctx.moveTo(x, TIMELINE_Y - 4);
      ctx.lineTo(x, TIMELINE_Y + 4);
      ctx.stroke();
      ctx.fillText(formatYear(year), x, TIMELINE_Y + 18);
    }

    // Draw month markers when zoomed in enough
    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (range < 15) {
      const showMonthLabels = range < 8; // Show month abbreviations when very zoomed in
      ctx.strokeStyle = COLORS.borderSubtle;
      ctx.fillStyle = '#4b5563'; // Slightly dimmer than year labels
      ctx.font = "9px 'GT America', system-ui, sans-serif";

      const monthStartYear = Math.floor(viewStart);
      const monthEndYear = Math.ceil(viewEnd);

      for (let year = monthStartYear; year <= monthEndYear; year++) {
        for (let month = 0; month < 12; month++) {
          const monthYear = year + month / 12;
          if (monthYear < viewStart || monthYear > viewEnd) continue;
          // Skip if this is January (already marked by year)
          if (month === 0) continue;

          const x = yearToX(monthYear);
          ctx.beginPath();
          ctx.moveTo(x, TIMELINE_Y - 2);
          ctx.lineTo(x, TIMELINE_Y + 2);
          ctx.stroke();

          if (showMonthLabels) {
            ctx.fillText(MONTH_NAMES[month], x, TIMELINE_Y + 28);
          }
        }
      }
    }

    // Draw main timeline - thin and subtle
    ctx.strokeStyle = COLORS.borderSubtle;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, TIMELINE_Y);
    ctx.lineTo(width, TIMELINE_Y);
    ctx.stroke();

    // Split scenes into dated and undated
    const datedScenes = scenes.filter(s => s.computedDate);
    const undatedScenes = scenes.filter(s => !s.computedDate);

    // Draw staging area for undated scenes
    if (undatedScenes.length > 0) {
      // Staging area background
      ctx.fillStyle = '#0a0a0e';
      ctx.fillRect(0, STAGING_AREA_Y - 2, width, STAGING_AREA_HEIGHT + 4);

      // Staging area label
      ctx.fillStyle = '#4b5563';
      ctx.font = "9px 'GT America', system-ui, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("UNDATED", 8, STAGING_AREA_Y + STAGING_AREA_HEIGHT / 2 + 3);

      // Draw undated scenes horizontally starting after the label
      const stagingStartX = 70;
      undatedScenes.forEach((scene, idx) => {
        const x = stagingStartX + idx * (STAGING_ICON_SIZE + STAGING_ICON_GAP);
        const y = STAGING_AREA_Y + STAGING_AREA_HEIGHT / 2;
        const isSelected = scene.id === selectedSceneId;
        const statusColor = SCENE_STATUS_COLORS[scene.status] || SCENE_STATUS_COLORS.draft;

        const sceneImg = imageCache.current.get(`scene_${scene.id}`);
        const iconSize = STAGING_ICON_SIZE;
        const halfSize = iconSize / 2;

        if (sceneImg) {
          // Draw thumbnail
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(x - halfSize, y - halfSize, iconSize, iconSize, 4);
          ctx.clip();
          ctx.drawImage(sceneImg, x - halfSize, y - halfSize, iconSize, iconSize);
          ctx.restore();

          // Border
          ctx.strokeStyle = isSelected ? '#ffffff' : statusColor;
          ctx.lineWidth = isSelected ? 2 : 1.5;
          ctx.beginPath();
          ctx.roundRect(x - halfSize, y - halfSize, iconSize, iconSize, 4);
          ctx.stroke();
        } else {
          // Draw camera icon fallback
          ctx.fillStyle = isSelected ? statusColor : COLORS.surface;
          ctx.beginPath();
          ctx.roundRect(x - halfSize, y - halfSize, iconSize, iconSize, 4);
          ctx.fill();

          ctx.strokeStyle = statusColor;
          ctx.lineWidth = isSelected ? 2 : 1.5;
          ctx.beginPath();
          ctx.roundRect(x - halfSize, y - halfSize, iconSize, iconSize, 4);
          ctx.stroke();

          // Camera icon
          ctx.strokeStyle = isSelected ? '#ffffff' : statusColor;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(x, y + 1, 4, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Selection glow
        if (isSelected) {
          ctx.shadowColor = statusColor;
          ctx.shadowBlur = 6;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.roundRect(x - halfSize - 2, y - halfSize - 2, iconSize + 4, iconSize + 4, 5);
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
      });

      // Separator line
      ctx.strokeStyle = COLORS.border;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, STAGING_AREA_Y + STAGING_AREA_HEIGHT + 2);
      ctx.lineTo(width, STAGING_AREA_Y + STAGING_AREA_HEIGHT + 2);
      ctx.stroke();
    }

    // Draw scene track background
    ctx.fillStyle = '#0d0d12';
    ctx.fillRect(0, SCENE_TRACK_Y - 5, width, SCENE_TRACK_HEIGHT + 10);

    // Scene track label
    ctx.fillStyle = COLORS.muted;
    ctx.font = "10px 'GT America', system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("SCENES", 8, SCENE_TRACK_Y + SCENE_TRACK_HEIGHT / 2 + 3);

    // Determine zoom level for scene rendering
    const zoomedIn = range < 30; // Show thumbnails when zoomed in

    // Draw dated scenes
    for (const scene of datedScenes) {
      if (!scene.computedDate) continue;

      const startYear = parseDate(scene.computedDate);
      if (startYear === null) continue;

      const x = yearToX(startYear);
      const y = SCENE_TRACK_Y + SCENE_TRACK_HEIGHT / 2;
      const isSelected = scene.id === selectedSceneId;
      const statusColor = SCENE_STATUS_COLORS[scene.status] || SCENE_STATUS_COLORS.draft;

      // If scene has date range, draw a bar
      if (scene.computedDateEnd) {
        const endYear = parseDate(scene.computedDateEnd);
        if (endYear !== null) {
          const x2 = yearToX(endYear);
          ctx.fillStyle = statusColor + '30'; // 30 = ~19% opacity
          ctx.fillRect(x, y - 8, x2 - x, 16);
          ctx.strokeStyle = statusColor + '60';
          ctx.lineWidth = 1;
          ctx.strokeRect(x, y - 8, x2 - x, 16);
        }
      }

      // Draw vertical dashed stem connecting scene to timeline
      ctx.strokeStyle = COLORS.borderSubtle;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, y + (zoomedIn ? SCENE_THUMB_SIZE / 2 + 4 : SCENE_ICON_SIZE / 2 + 4));
      ctx.lineTo(x, TIMELINE_Y - 20);
      ctx.stroke();
      ctx.setLineDash([]);

      const sceneImg = imageCache.current.get(`scene_${scene.id}`);

      if (zoomedIn && sceneImg) {
        // Draw thumbnail
        const thumbSize = SCENE_THUMB_SIZE;
        const thumbRadius = 6;

        // Draw rounded rect clipped thumbnail
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(x - thumbSize / 2, y - thumbSize / 2, thumbSize, thumbSize, thumbRadius);
        ctx.clip();
        ctx.drawImage(sceneImg, x - thumbSize / 2, y - thumbSize / 2, thumbSize, thumbSize);
        ctx.restore();

        // Border
        ctx.strokeStyle = isSelected ? '#ffffff' : statusColor;
        ctx.lineWidth = isSelected ? 2.5 : 2;
        ctx.beginPath();
        ctx.roundRect(x - thumbSize / 2, y - thumbSize / 2, thumbSize, thumbSize, thumbRadius);
        ctx.stroke();

        // Status dot
        ctx.fillStyle = statusColor;
        ctx.beginPath();
        ctx.arc(x + thumbSize / 2 - 4, y - thumbSize / 2 + 4, 4, 0, Math.PI * 2);
        ctx.fill();

        // Selection glow
        if (isSelected) {
          ctx.shadowColor = statusColor;
          ctx.shadowBlur = 10;
          ctx.strokeStyle = statusColor;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.roundRect(x - thumbSize / 2 - 2, y - thumbSize / 2 - 2, thumbSize + 4, thumbSize + 4, thumbRadius + 1);
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
      } else {
        // Draw camera icon
        const iconSize = SCENE_ICON_SIZE;
        const halfSize = iconSize / 2;

        // Camera body background
        ctx.fillStyle = isSelected ? statusColor : COLORS.surface;
        ctx.beginPath();
        ctx.roundRect(x - halfSize, y - halfSize, iconSize, iconSize, 4);
        ctx.fill();

        // Camera body border
        ctx.strokeStyle = statusColor;
        ctx.lineWidth = isSelected ? 2 : 1.5;
        ctx.beginPath();
        ctx.roundRect(x - halfSize, y - halfSize, iconSize, iconSize, 4);
        ctx.stroke();

        // Camera icon paths
        ctx.strokeStyle = isSelected ? '#ffffff' : statusColor;
        ctx.lineWidth = 1.5;

        // Camera lens
        ctx.beginPath();
        ctx.arc(x, y + 1, 5, 0, Math.PI * 2);
        ctx.stroke();

        // Camera flash
        ctx.beginPath();
        ctx.moveTo(x + 4, y - 5);
        ctx.lineTo(x + 6, y - 5);
        ctx.stroke();

        // Selection glow
        if (isSelected) {
          ctx.shadowColor = statusColor;
          ctx.shadowBlur = 8;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.roundRect(x - halfSize - 2, y - halfSize - 2, iconSize + 4, iconSize + 4, 5);
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
      }
    }

    // Draw visible people - reverse order so most recently selected appears at bottom (closest to timeline)
    const visiblePeopleArray = visiblePeople
      .map((id) => people.find((p) => p.id === id))
      .filter((p): p is TimelinePerson => p !== undefined)
      .reverse(); // Reverse so first selected (oldest) is at top, most recent at bottom
    visiblePeopleArray.forEach((person, idx) => {
      const birthYear = parseDate(person.birthDate);
      const deathYear = parseDate(person.deathDate) || 2024;
      if (birthYear === null) return;

      const x1 = yearToX(birthYear);
      const x2 = yearToX(deathYear);
      const y = PERSON_START_Y + idx * PERSON_ROW_HEIGHT;
      const barHeight = 22;
      const photoRadius = PERSON_PHOTO_SIZE / 2;

      // Skip if bar is completely off screen
      if (x2 < 0 || x1 > width) return;

      // Person lifespan bar - muted teal shades
      const hue = 170 + (idx * 15) % 40; // Teal range
      ctx.fillStyle = `hsla(${hue}, 40%, 40%, 0.4)`;
      ctx.fillRect(x1, y, x2 - x1, barHeight);

      // Draw photo or initials circle - pin to left edge if bar extends off screen
      const minPhotoX = photoRadius + 4; // Minimum X to keep photo on screen
      const maxPhotoX = x2 - photoRadius - 4; // Don't go past end of bar
      const naturalPhotoX = x1 + photoRadius + 2;
      const photoX = Math.max(minPhotoX, Math.min(naturalPhotoX, maxPhotoX));
      const photoY = y + barHeight / 2;
      const cachedImg = imageCache.current.get(`person_${person.id}`);

      if (cachedImg) {
        // Draw circular clipped image
        ctx.save();
        ctx.beginPath();
        ctx.arc(photoX, photoY, photoRadius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(
          cachedImg,
          photoX - photoRadius,
          photoY - photoRadius,
          PERSON_PHOTO_SIZE,
          PERSON_PHOTO_SIZE
        );
        ctx.restore();

        // Photo border
        ctx.strokeStyle = `hsla(${hue}, 50%, 55%, 0.8)`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(photoX, photoY, photoRadius, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Draw initials fallback
        ctx.fillStyle = `hsl(${hue}, 40%, 35%)`;
        ctx.beginPath();
        ctx.arc(photoX, photoY, photoRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.font = "bold 9px 'GT America', system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(getInitials(person.name), photoX, photoY);
        ctx.textBaseline = "alphabetic";
      }

      // Person name (offset from photo position)
      ctx.fillStyle = '#e5e7eb';
      ctx.font = "10px 'GT America', system-ui, sans-serif";
      ctx.textAlign = "left";
      const nameX = photoX + photoRadius + 6;
      ctx.fillText(person.name, nameX, y + barHeight / 2 + 4);

      // Death marker (if applicable)
      if (person.deathDate) {
        ctx.fillStyle = `hsla(${hue}, 50%, 55%, 1)`;
        ctx.beginPath();
        ctx.arc(x2, y + barHeight / 2, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw scene dots for this person (scenes where they are tagged)
      for (const scene of scenes) {
        if (!scene.computedDate || !scene.characters) continue;

        // Check if person is tagged in this scene
        const isTagged = scene.characters.some(c => c.personId === person.id);
        if (!isTagged) continue;

        const sceneYear = parseDate(scene.computedDate);
        if (sceneYear === null) continue;

        const sceneX = yearToX(sceneYear);

        // Only draw if within visible range
        if (sceneX >= 0 && sceneX <= width) {
          // Draw small dot below the person bar
          ctx.beginPath();
          ctx.arc(sceneX, y + barHeight + 5, 3, 0, Math.PI * 2);
          ctx.fillStyle = "#14B8A6"; // Teal to match scene color
          ctx.fill();
        }
      }
    });

    // Prepare events with positions for label staggering
    const eventsWithPos = events
      .map((event) => {
        const year = parseDate(event.date);
        if (year === null) return null;
        const x = yearToX(year);
        return { event, x, year };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null)
      .sort((a, b) => a.x - b.x);

    // Calculate which labels to show based on zoom level
    const visibleRange = viewEnd - viewStart;
    const showAllLabels = visibleRange < 15; // Show all only when very zoomed in
    const showSomeLabels = visibleRange < 50; // Show some when in medium zoom

    // Smart collision detection - assign stem heights based on proximity
    // Track occupied label positions at each height level
    type LabelSlot = { x: number; width: number; height: number };
    const occupiedSlots: LabelSlot[] = [];
    const STEM_HEIGHT_OPTIONS = [45, 75, 105, 135]; // More height options
    const LABEL_PADDING = 10; // Horizontal padding between labels

    ctx.font = `${LABEL_FONT_SIZE}px 'GT America', system-ui, sans-serif`;

    const labelPositions: { event: TimelineEvent; x: number; stemHeight: number; showLabel: boolean; isHovered: boolean }[] = [];

    for (let i = 0; i < eventsWithPos.length; i++) {
      const { event, x } = eventsWithPos[i];
      const isHovered = event.id === hoveredEventId;

      // Determine if label should show based on zoom
      let showLabel = isHovered; // Always show hovered
      if (!showLabel && showAllLabels) {
        showLabel = true;
      } else if (!showLabel && showSomeLabels) {
        // When in medium zoom, show based on spacing
        const lastShown = [...labelPositions].reverse().find(l => l.showLabel && !l.isHovered);
        if (!lastShown || Math.abs(x - lastShown.x) > MIN_LABEL_SPACING) {
          showLabel = true;
        }
      }

      // Find the best stem height that doesn't overlap
      let bestHeight = STEM_HEIGHT_OPTIONS[0];
      if (showLabel) {
        // Measure label width
        let title = event.title;
        const maxWidth = 120;
        if (ctx.measureText(title).width > maxWidth) {
          while (ctx.measureText(title + "…").width > maxWidth && title.length > 0) {
            title = title.slice(0, -1);
          }
          title = title.trim() + "…";
        }
        const labelWidth = ctx.measureText(title).width;
        const labelLeft = x - labelWidth / 2 - LABEL_PADDING;
        const labelRight = x + labelWidth / 2 + LABEL_PADDING;

        // Try each height level to find one without collision
        for (const heightOption of STEM_HEIGHT_OPTIONS) {
          let hasCollision = false;
          for (const slot of occupiedSlots) {
            if (slot.height === heightOption) {
              // Check horizontal overlap
              if (labelLeft < slot.x + slot.width && labelRight > slot.x) {
                hasCollision = true;
                break;
              }
            }
          }
          if (!hasCollision) {
            bestHeight = heightOption;
            break;
          }
        }

        // Register this label's position
        occupiedSlots.push({ x: labelLeft, width: labelRight - labelLeft, height: bestHeight });
      }

      labelPositions.push({ event, x, stemHeight: bestHeight, showLabel, isHovered });
    }

    // Draw event stems and labels
    for (const { event, x, stemHeight, showLabel, isHovered } of labelPositions) {
      const hasEventImage = imageCache.current.has(`event_${event.id}`);
      const eventMarkerRadius = isHovered ? 16 : 14;
      // Stem starts from top of marker (image or dot)
      const stemStart = hasEventImage ? TIMELINE_Y - eventMarkerRadius - 2 : TIMELINE_Y - 6;

      if (showLabel) {
        // Draw stem
        ctx.strokeStyle = isHovered ? COLORS.primary : COLORS.stem;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, stemStart);
        ctx.lineTo(x, TIMELINE_Y - stemHeight);
        ctx.stroke();

        // Draw label
        ctx.fillStyle = isHovered ? COLORS.labelTextHover : COLORS.labelText;
        ctx.font = `${LABEL_FONT_SIZE}px 'GT America', system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";

        // Truncate long titles
        let title = event.title;
        const maxWidth = 120;
        if (ctx.measureText(title).width > maxWidth) {
          while (ctx.measureText(title + "…").width > maxWidth && title.length > 0) {
            title = title.slice(0, -1);
          }
          title = title.trim() + "…";
        }

        ctx.fillText(title, x, TIMELINE_Y - stemHeight - 3);
        ctx.textBaseline = "alphabetic";
      }

      // Draw event marker (image or dot)
      const eventImg = imageCache.current.get(`event_${event.id}`);
      const imgMarkerRadius = isHovered ? 16 : 14;

      if (eventImg) {
        // Draw circular image
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, TIMELINE_Y, imgMarkerRadius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(
          eventImg,
          x - imgMarkerRadius,
          TIMELINE_Y - imgMarkerRadius,
          imgMarkerRadius * 2,
          imgMarkerRadius * 2
        );
        ctx.restore();

        // Draw border ring
        ctx.strokeStyle = isHovered ? '#ffffff' : COLORS.primary;
        ctx.lineWidth = isHovered ? 2.5 : 2;
        ctx.beginPath();
        ctx.arc(x, TIMELINE_Y, imgMarkerRadius, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Fallback to dot
        ctx.fillStyle = COLORS.primary;
        ctx.beginPath();
        ctx.arc(x, TIMELINE_Y, isHovered ? 6 : 5, 0, Math.PI * 2);
        ctx.fill();

        // Subtle glow
        ctx.strokeStyle = COLORS.primaryMuted;
        ctx.lineWidth = isHovered ? 2 : 1;
        ctx.stroke();
      }
    }
  }, [dimensions, viewStart, viewEnd, events, eras, people, scenes, visiblePeople, selectedSceneId, yearToX, imagesLoaded, hoveredEventId]);

  useEffect(() => {
    draw();
  }, [draw]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseYear = xToYear(mouseX);

      const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
      const range = viewEnd - viewStart;
      // Allow zoom down to ~1 month (0.08 years) to see dense event clusters
      const newRange = Math.max(0.08, Math.min(MAX_YEAR - MIN_YEAR, range * zoomFactor));

      const mouseRatio = mouseX / dimensions.width;
      const newStart = Math.max(MIN_YEAR, mouseYear - newRange * mouseRatio);
      const newEnd = Math.min(MAX_YEAR, newStart + newRange);

      setViewStart(newStart);
      setViewEnd(newEnd);
    },
    [xToYear, viewStart, viewEnd, dimensions.width]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setDragStart({ x: e.clientX, viewStart });
    },
    [viewStart]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      if (isDragging) {
        const dx = e.clientX - dragStart.x;
        const yearDelta = (dx / dimensions.width) * (viewEnd - viewStart);
        const newStart = Math.max(MIN_YEAR, Math.min(MAX_YEAR - (viewEnd - viewStart), dragStart.viewStart - yearDelta));
        const newEnd = newStart + (viewEnd - viewStart);

        setViewStart(newStart);
        setViewEnd(newEnd);
        return;
      }

      // Check for hover on undated scenes in staging area first
      const undatedScenes = scenes.filter(s => !s.computedDate);
      const stagingStartX = 70;
      for (let idx = 0; idx < undatedScenes.length; idx++) {
        const scene = undatedScenes[idx];
        const x = stagingStartX + idx * (STAGING_ICON_SIZE + STAGING_ICON_GAP);
        const y = STAGING_AREA_Y + STAGING_AREA_HEIGHT / 2;
        const hitSize = STAGING_ICON_SIZE / 2 + 4;

        if (
          mouseX >= x - hitSize &&
          mouseX <= x + hitSize &&
          mouseY >= y - hitSize &&
          mouseY <= y + hitSize
        ) {
          onHover({ type: "scene", item: scene, x: e.clientX, y: e.clientY });
          return;
        }
      }

      // Check for hover on dated scenes in timeline
      const range = viewEnd - viewStart;
      const zoomedIn = range < 30;
      const datedScenes = scenes.filter(s => s.computedDate);
      for (const scene of datedScenes) {
        const startYear = parseDate(scene.computedDate);
        if (startYear === null) continue;

        const x = yearToX(startYear);
        const y = SCENE_TRACK_Y + SCENE_TRACK_HEIGHT / 2;
        const hitSize = zoomedIn ? SCENE_THUMB_SIZE / 2 + 4 : SCENE_ICON_SIZE / 2 + 4;

        if (
          mouseX >= x - hitSize &&
          mouseX <= x + hitSize &&
          mouseY >= y - hitSize &&
          mouseY <= y + hitSize
        ) {
          onHover({ type: "scene", item: scene, x: e.clientX, y: e.clientY });
          return;
        }
      }

      // Check for hover on events
      for (const event of events) {
        const year = parseDate(event.date);
        if (year === null) continue;

        const x = yearToX(year);
        const dist = Math.sqrt((mouseX - x) ** 2 + (mouseY - TIMELINE_Y) ** 2);
        // Larger hit area for events with images
        const hasImage = imageCache.current.has(`event_${event.id}`);
        const hitRadius = hasImage ? 18 : 15;

        if (dist < hitRadius) {
          setHoveredEventId(event.id);
          onHover({ type: "event", item: event, x: e.clientX, y: e.clientY });
          return;
        }
      }

      // Clear hovered event if not hovering any
      setHoveredEventId(null);

      // Check for hover on eras
      for (const era of eras) {
        const startYear = parseDate(era.startDate);
        const endYear = parseDate(era.endDate);
        if (startYear === null || endYear === null) continue;

        const x1 = yearToX(startYear);
        const x2 = yearToX(endYear);

        if (
          mouseX >= x1 &&
          mouseX <= x2 &&
          mouseY >= TIMELINE_Y - 25 &&
          mouseY <= TIMELINE_Y + 25
        ) {
          onHover({ type: "era", item: era, x: e.clientX, y: e.clientY });
          return;
        }
      }

      // Check for hover on people
      const visiblePeopleArrayHover = visiblePeople
        .map((id) => people.find((p) => p.id === id))
        .filter((p): p is TimelinePerson => p !== undefined)
        .reverse();
      for (let idx = 0; idx < visiblePeopleArrayHover.length; idx++) {
        const person = visiblePeopleArrayHover[idx];
        const birthYear = parseDate(person.birthDate);
        const deathYear = parseDate(person.deathDate) || 2024;
        if (birthYear === null) continue;

        const x1 = yearToX(birthYear);
        const x2 = yearToX(deathYear);
        const y = PERSON_START_Y + idx * PERSON_ROW_HEIGHT;

        if (mouseX >= x1 && mouseX <= x2 && mouseY >= y && mouseY <= y + 22) {
          onHover({ type: "person", item: person, x: e.clientX, y: e.clientY });
          return;
        }
      }

      onHover(null);
    },
    [isDragging, dragStart, dimensions.width, viewStart, viewEnd, events, eras, people, scenes, visiblePeople, yearToX, onHover]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Check for clicks on undated scenes in staging area first
      const undatedScenes = scenes.filter(s => !s.computedDate);
      const stagingStartX = 70;
      for (let idx = 0; idx < undatedScenes.length; idx++) {
        const scene = undatedScenes[idx];
        const x = stagingStartX + idx * (STAGING_ICON_SIZE + STAGING_ICON_GAP);
        const y = STAGING_AREA_Y + STAGING_AREA_HEIGHT / 2;
        const hitSize = STAGING_ICON_SIZE / 2 + 4;

        if (
          mouseX >= x - hitSize &&
          mouseX <= x + hitSize &&
          mouseY >= y - hitSize &&
          mouseY <= y + hitSize
        ) {
          onSceneClick(scene.id, e.clientX, e.clientY);
          return;
        }
      }

      // Check for clicks on dated scenes
      const range = viewEnd - viewStart;
      const zoomedIn = range < 30;
      const datedScenes = scenes.filter(s => s.computedDate);
      for (const scene of datedScenes) {
        const startYear = parseDate(scene.computedDate);
        if (startYear === null) continue;

        const x = yearToX(startYear);
        const y = SCENE_TRACK_Y + SCENE_TRACK_HEIGHT / 2;
        const hitSize = zoomedIn ? SCENE_THUMB_SIZE / 2 + 4 : SCENE_ICON_SIZE / 2 + 4;

        if (
          mouseX >= x - hitSize &&
          mouseX <= x + hitSize &&
          mouseY >= y - hitSize &&
          mouseY <= y + hitSize
        ) {
          onSceneClick(scene.id, e.clientX, e.clientY);
          return;
        }
      }

      for (const event of events) {
        const year = parseDate(event.date);
        if (year === null) continue;

        const x = yearToX(year);
        const dist = Math.sqrt((mouseX - x) ** 2 + (mouseY - TIMELINE_Y) ** 2);
        // Larger hit area for events with images
        const hasImage = imageCache.current.has(`event_${event.id}`);
        const hitRadius = hasImage ? 16 : 10;

        if (dist < hitRadius) {
          onClick({ type: "event", item: event, x: e.clientX, y: e.clientY });
          return;
        }
      }

      for (const era of eras) {
        const startYear = parseDate(era.startDate);
        const endYear = parseDate(era.endDate);
        if (startYear === null || endYear === null) continue;

        const x1 = yearToX(startYear);
        const x2 = yearToX(endYear);

        if (
          mouseX >= x1 &&
          mouseX <= x2 &&
          mouseY >= TIMELINE_Y - 25 &&
          mouseY <= TIMELINE_Y + 25
        ) {
          onClick({ type: "era", item: era, x: e.clientX, y: e.clientY });
          return;
        }
      }
    },
    [events, eras, scenes, viewStart, viewEnd, yearToX, onClick, onSceneClick]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!onContextMenu) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const year = xToYear(mouseX);

      // Convert year to date string (use Jan 1 of that year)
      const yearInt = Math.floor(year);
      const monthFraction = (year - yearInt) * 12;
      const month = Math.floor(monthFraction) + 1;
      const day = Math.floor((monthFraction - Math.floor(monthFraction)) * 30) + 1;

      const dateStr = `${yearInt}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      onContextMenu(dateStr, e.clientX, e.clientY);
    },
    [xToYear, onContextMenu]
  );

  return (
    <div ref={containerRef} className="w-full h-full overflow-hidden">
      <canvas
        ref={canvasRef}
        className={`w-full h-full ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      />
    </div>
  );
});
